import { gql, useQuery } from '@apollo/client'
import { ArrowDownward, ArrowUpward, Today } from '@mui/icons-material'
import { IconButton, Skeleton, TextField, Typography } from '@mui/material'
import { Query } from '@promoboxx/graphql-gateway-types'
import localStorageStore from '@promoboxx/use-filter/dist/store/localStorageStore'
import useSimpleFilter from '@promoboxx/use-filter/dist/useSimpleFilter'
import I18n from 'i18n-js'
import moment, { Moment, MomentInput } from 'moment'
import { useEffect, useMemo } from 'react'
import { useHistory } from 'react-router'

import FilterContentLayoutWrapper from '@src/components/shared/FilterContentLayoutWrapper'
import FilterToolbar from '@src/components/shared/FilterToolbar'
import cloudinaryRef from '@src/lib/Cloudinary'
import MenuItemWithCheckbox from '@src/lib/MenuItemWithCheckbox'
import useCurrentRetailerAccount from '@src/lib/useCurrentRetailerAccount'

import organicCampaignPaths from '../organicCampaignPaths'

import OrganicCampaignDetailsAssets from './OrganicCampaignDetailsAssets'
import { OrganicCampaignDetailsFilters } from './organicCampaignDetailsFilter'
import OrganicCampaignDetailsShareSettings from './OrganicCampaignDetailsShareSettings'

const OrganicCampaignDetailsLayout: React.FC = () => {
  const params = organicCampaignPaths.details.useParams()
  const history = useHistory()
  const { ensureCurrentRetailerAccount } = useCurrentRetailerAccount()

  const { data, loading } = useQuery<Query>(GET_CAMPAIGN_QUERY, {
    variables: {
      campaignId: params.campaignId,
    },
  })

  const validTabs = useMemo(() => {
    if (loading) {
      return []
    }

    const validChannels =
      data?.getCampaign?.share_settings_count_per_share_type?.map(
        (x) => x.channel,
      )

    return ALL_TABS.filter(
      (x) => x.label === 'downloadables' || validChannels?.includes(x.label),
    )
  }, [data?.getCampaign?.share_settings_count_per_share_type, loading])

  useEffect(() => {
    if (loading) {
      return
    }

    if (params.channel) {
      return
    }

    const firstChannel = validTabs[0]

    if (!firstChannel) {
      console.error('Uhhhhhhhhh')
      return
    }

    history.replace(
      organicCampaignPaths.details.build({
        retailerAccountSlug: params.retailerAccountSlug,
        campaignId: params.campaignId,
        channel: firstChannel.label,
      }),
    )
  }, [
    history,
    loading,
    params.campaignId,
    params.channel,
    params.retailerAccountSlug,
    validTabs,
  ])

  const filterApi: OrganicCampaignDetailsFilters = useSimpleFilter(
    'shareSettings',
    {
      defaultFilterInfo: {
        filter: {
          sortField: 'updated',
          sortDirection: 'desc',
        },
      },
      store: localStorageStore,
    },
  )

  const retailerForCampaign = useMemo(() => {
    if (!data?.getCampaign?.brand?.id) {
      return
    }

    return ensureCurrentRetailerAccount.retailers?.find(
      (x) => String(x.brand?.id) === String(data?.getCampaign?.brand?.id),
    )
  }, [data?.getCampaign?.brand?.id, ensureCurrentRetailerAccount.retailers])

  return (
    <>
      <FilterContentLayoutWrapper
        title={loading ? <Skeleton /> : data?.getCampaign?.name || ''}
        description={
          loading ? (
            <>
              <Skeleton width="10%" />
              <Skeleton width="15%" />
              <Skeleton width="40%" />
            </>
          ) : (
            <>
              <Typography color="text.secondary" variant="body2">
                {data?.getCampaign?.brand?.name}
                <br />
                <Today
                  fontSize="inherit"
                  color="inherit"
                  sx={{ verticalAlign: 'middle' }}
                />{' '}
                {formatDaysRemainingCopy(
                  new Date(),
                  moment(data?.getCampaign?.end_date),
                )}
              </Typography>

              <Typography>{data?.getCampaign?.description}</Typography>
            </>
          )
        }
        left={
          loading ? (
            <Skeleton width={180} height={100} />
          ) : (
            <>
              <img
                alt={data?.getCampaign?.name || ''}
                src={cloudinaryRef.getCloudinaryUrl(
                  data?.getCampaign?.logo_public_id || '',
                  {
                    background: 'auto:predominant',
                    crop: 'pad',
                    width: 180 * 2,
                    height: 100 * 2,
                    fetch_format: 'jpg',
                    quality: 'auto',
                  },
                )}
                style={{
                  flexBasis: '180px',
                  width: 180,
                  height: 100,
                }}
              />
            </>
          )
        }
        slot={{
          filter: (
            <>
              <FilterToolbar
                filterItems={[
                  <TextField
                    select
                    fullWidth
                    value={filterApi.filterInfo.filter.sortField}
                    label="Sort by ..."
                    onChange={(event) => {
                      filterApi.updateFilter(
                        { sortField: event.target.value },
                        true,
                      )
                    }}
                  >
                    <MenuItemWithCheckbox value="created">
                      Created
                    </MenuItemWithCheckbox>
                    <MenuItemWithCheckbox value="updated">
                      Last Updated
                    </MenuItemWithCheckbox>
                    <MenuItemWithCheckbox value="name">
                      Name
                    </MenuItemWithCheckbox>
                    <MenuItemWithCheckbox value="format">
                      Format
                    </MenuItemWithCheckbox>
                  </TextField>,

                  <IconButton
                    size="small"
                    onClick={() => {
                      const newDirection =
                        filterApi.filterInfo.filter.sortDirection === 'asc'
                          ? 'desc'
                          : 'asc'
                      filterApi.updateFilter(
                        { sortDirection: newDirection },
                        true,
                      )
                    }}
                  >
                    {filterApi.filterInfo.filter.sortDirection === 'desc' ? (
                      <ArrowUpward fontSize="small" />
                    ) : (
                      <ArrowDownward fontSize="small" />
                    )}
                  </IconButton>,
                ]}
                onClear={() => {
                  filterApi.resetFilter(true)
                }}
              />
            </>
          ),
        }}
        slotProps={{
          tab: {
            selectedTab: params.channel,
            tabs: validTabs.map((x) => {
              return {
                value: x.label,
                label: x.label,
                to: organicCampaignPaths.details.build({
                  retailerAccountSlug: params.retailerAccountSlug,
                  campaignId: params.campaignId,
                  channel: x.label,
                }),
              }
            }),
          },
        }}
      >
        {params.channel === 'downloadables' || params.channel === 'tiktok' ? (
          <OrganicCampaignDetailsAssets
            campaignId={params.campaignId}
            shareType={params.channel}
            filterApi={filterApi}
          />
        ) : params.channel ? (
          <OrganicCampaignDetailsShareSettings
            campaignId={params.campaignId}
            shareType={params.channel}
            filterApi={filterApi}
            retailerId={retailerForCampaign?.id}
          />
        ) : undefined}
      </FilterContentLayoutWrapper>
    </>
  )
}

const GET_CAMPAIGN_QUERY = gql`
  query getCampaignOrganicDetails($campaignId: ID!) {
    getCampaign(campaignId: $campaignId) {
      id
      name

      start_date
      end_date

      description

      logo_public_id

      share_settings_count_per_share_type {
        channel
        count
      }

      brand {
        id
        name
        logo_url
      }
    }
  }
`

// TODO This is duplicated.
const formatDaysRemainingCopy = (now: MomentInput, endDate: Moment) => {
  let daysRemaining = endDate.diff(now, 'days')

  if (Number.isNaN(daysRemaining)) {
    return null
  }

  return endDate.calendar(null, {
    sameDay: `[${I18n.t('content.endsToday')}] LT`,
    nextDay: `[${I18n.t('content.endsTomorrow')}] LT`,
    nextWeek: `[${I18n.t('content.xDaysRemaining', {
      count: daysRemaining,
    })}]`,
    sameElse: `[${I18n.t('content.xDaysRemaining', {
      count: daysRemaining,
    })}]`,
  })
}

const ALL_TABS = [
  {
    label: 'facebook',
  },
  {
    label: 'instagram',
  },
  {
    label: 'downloadables',
  },
  {
    label: 'twitter',
  },
  {
    label: 'tiktok',
  },
  {
    label: 'embed',
  },
  {
    label: 'email',
  },
]

export default OrganicCampaignDetailsLayout
